// package
import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import moment from "moment";

// component
import AgStatus from "elements/System/AgStatus";
import CustomDatepicker from "elements/System/Datepicker";
import UserPost from "elements/Chart/UserPost";
import Localities from "elements/Chart/Localities";
import Incidents from "elements/Chart/Incidents";
import SubLocalities from "elements/Chart/SubLocalities";

// api import
import { api, common } from "includes";

const adminLabel = (a: any) =>
  [a?.firstName, a?.lastName].filter(Boolean).join(" ") || a?.email || "Unknown";

const Analytics = () => {
  // State
  //
  // The page used to hold a single `selectedDate` that was never wired to a
  // control, and shipped it as `params`, which api.call does not read — so no
  // date ever left the browser, and the endpoint ignored dates anyway. Both
  // ends are real now: a range plus an optional admin.
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [adminId, setAdminId] = useState<any>(null);

  const [process, setProcess] = useState<any>({ result: 100 });
  const [result, setResult] = useState<any>({
    adminPosts: [],
    localityFollowers: [],
    incidentViews: [],
    subLocalityFollowers: [],
    admins: [],
    summary: {},
  });

  // state
  //
  // Gated on the role, not the 'anr' permission. Analytics is super-only as of
  // 2026-08-13, and existing admin accounts still carry a stored 'anr' that
  // removing it from the default permission set does not retroactively strip —
  // so a permission check would still let yesterday's admins in. The API route
  // is locked to super too; this just stops them landing on a page that would
  // only 403 at them.
  const [authData] = useState<any>(common.getAuth());
  // effect
  useEffect(() => {
    if (authData?.role !== "super") {
      common.redirect("app/home");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API
  const getData = useCallback(() => {
    processState("result", 100);

    const parts: string[] = [];
    if (startDate) parts.push(`startDate=${encodeURIComponent(moment(startDate).format("YYYY-MM-DD"))}`);
    if (endDate) parts.push(`endDate=${encodeURIComponent(moment(endDate).format("YYYY-MM-DD"))}`);
    if (adminId?.value) parts.push(`createdBy=${encodeURIComponent(adminId.value)}`);

    const data: any = {
      url: "admin/analytic",
      method: "GET",
      auth: "token",
      query: parts.length ? `?${parts.join("&")}` : "",
    };
    api.call(data, (res: any) => {
      processState("result", res.status);
      if (res.status === 200) {
        const payload = res.data.data;
        setResult({
          adminPosts: payload.adminPosts || [],
          localityFollowers: payload.localityFollowers || [],
          incidentViews: payload.incidentViews || [],
          subLocalityFollowers: payload.subLocalityFollowers || [],
          admins: payload.admins || [],
          summary: payload.summary || {},
        });
      }
    });
  }, [startDate, endDate, adminId]);

  useEffect(() => {
    getData();
  }, [getData]);

  // Callback function for AgStatus component
  const onAgCallBack = () => getData();

  // Update process state for loading/error handling
  const processState = (label: any, value: any) => {
    setProcess((prev: any) => ({
      ...prev,
      [label]: value,
    }));
  };

  const adminOptions = useMemo(
    () =>
      (result.admins || []).map((a: any) => ({
        value: a._id,
        label: `${adminLabel(a)} · ${a.email}`,
      })),
    [result.admins]
  );

  const hasFilter = Boolean(startDate || endDate || adminId);

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setAdminId(null);
  };

  // What the numbers on screen actually cover, in words — the controls alone
  // can't say whether a range was applied before or after the last fetch.
  const rangeLabel = () => {
    if (!startDate && !endDate) return "All time";
    const from = startDate ? moment(startDate).format("DD MMM YYYY") : "the beginning";
    const to = endDate ? moment(endDate).format("DD MMM YYYY") : "today";
    return `${from} → ${to}`;
  };

  return (
    <Fragment>
      <Row>
        <Col>
          <div className="page-title-box">
            <div className="page-title-right">
              <form className="d-flex">
                <Link to="#" className="btn btn-success ms-2 flex-shrink-0" onClick={() => getData()}>
                  <i className="ri-refresh-line" /> Refresh
                </Link>
              </form>
            </div>
            <h4 className="page-title">Analytics</h4>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="mb-2">
            <Card.Body className="py-2">
              <Row className="g-2 align-items-end">
                <Col md={3} sm={6}>
                  <label className="form-label mb-1">From</label>
                  <CustomDatepicker
                    value={startDate}
                    inputClass="form-control-light"
                    maxDate={endDate || undefined}
                    onChange={(date: Date) => setStartDate(date)}
                  />
                </Col>
                <Col md={3} sm={6}>
                  <label className="form-label mb-1">To</label>
                  <CustomDatepicker
                    value={endDate}
                    inputClass="form-control-light"
                    minDate={startDate || undefined}
                    onChange={(date: Date) => setEndDate(date)}
                  />
                </Col>
                <Col md={4} sm={9}>
                  <label className="form-label mb-1">Created by</label>
                  <Select
                    className="custom-select-box"
                    classNamePrefix="react-select"
                    options={adminOptions}
                    value={adminId}
                    isClearable
                    placeholder="All admins"
                    onChange={(v: any) => setAdminId(v)}
                  />
                </Col>
                <Col md={2} sm={3}>
                  <button
                    type="button"
                    className="btn btn-light w-100"
                    disabled={!hasFilter}
                    onClick={clearFilters}
                  >
                    Clear
                  </button>
                </Col>
              </Row>

              <div className="mt-2 text-muted">
                <strong>{result.summary?.totalIncidents ?? 0}</strong> incident
                {result.summary?.totalIncidents === 1 ? "" : "s"} · {rangeLabel()}
                {adminId ? ` · ${adminId.label}` : ""}
                {/* Said out loud because the follower charts below sit inside
                    the same filtered page and would otherwise look filtered. */}
                <span className="ms-2">
                  Follower totals are all-time and ignore this range.
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {process.result !== 200 ? (
        <AgStatus
          process={process.result}
          message={{
            100: "Loading... Please wait!",
            404: "Something went wrong! Please Refresh Again",
          }}
          button={{ 400: "Refresh", 404: "Refresh" }}
          size="l"
          onAgCallBack={onAgCallBack} // Callback to refresh data
        />
      ) : (
        <Fragment>
          <Row>
            <Col xl={6} lg={12}>
              <UserPost data={result.adminPosts} />
              <Incidents data={result.incidentViews} />
            </Col>
            <Col xl={6} lg={12}>
              <Localities data={result.localityFollowers} />
              <SubLocalities data={result.subLocalityFollowers} />
            </Col>
          </Row>
        </Fragment>
      )}
    </Fragment>
  );
};

export default Analytics;
